import React from 'react';
import { ArrowLeft, Trash2, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const JustConverterPrivacyPolicy = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="privacy-policy">
      <div className="privacy-policy-container">
        <button className="back-button" onClick={handleBack}>
          <ArrowLeft size={20} />
          Back to Home
        </button>
        
        <div className="privacy-policy-content">
          <h1>JustConverter Privacy Policy</h1>
          <p className="last-updated">Last updated: March 15, 2025</p>
          
          <section>
            <p>
              This privacy policy applies to the JustConverter app (hereby referred to as "Application") for mobile devices that was created by Justlabs (hereby referred to as "Service Provider") as a Free service. This service is intended for use "AS IS".
            </p>
          </section>

          <section>
            <h2>Information Collection and Use</h2>
            <p>
              The Application collects information when you download and use it. This information may include information such as:
            </p>
            <ul>
              <li>Your device's Internet Protocol address (e.g. IP address)</li>
              <li>The pages of the Application that you visit, the time and date of your visit, the time spent on those pages</li>
              <li>The time spent on the Application</li>
              <li>The operating system you use on your mobile device</li>
            </ul>
            <p>The Application does not gather precise information about the location of your mobile device.</p>
            
            <p>The Application collects your device's location, which helps the Service Provider determine your approximate geographical location and make use of in below ways:</p>
            <ul>
              <li><strong>Geolocation Services:</strong> The Service Provider utilizes location data to provide features such as personalized content, relevant recommendations, and location-based services.</li>
              <li><strong>Analytics and Improvements:</strong> Aggregated and anonymized location data helps the Service Provider to analyze user behavior, identify trends, and improve the overall performance and functionality of the Application.</li>
              <li><strong>Third-Party Services:</strong> Periodically, the Service Provider may transmit anonymized location data to external services. These services assist them in enhancing the Application and optimizing their offerings.</li>
            </ul>
            
            <p>
              The Service Provider may use the information you provided to contact you from time to time to provide you with important information, required notices and marketing promotions.
            </p>
            
            <p>
              For a better experience, while using the Application, the Service Provider may require you to provide us with certain personally identifiable information. The information that the Service Provider request will be retained by them and used as described in this privacy policy.
            </p>
          </section>

          <section>
            <h2>Third Party Access</h2>
            <p>
              Only aggregated, anonymized data is periodically transmitted to external services to aid the Service Provider in improving the Application and their service. The Service Provider may share your information with third parties in the ways that are described in this privacy statement.
            </p>
            
            <p>
              Please note that the Application utilizes third-party services that have their own Privacy Policy about handling data. Below are the links to the Privacy Policy of the third-party service providers used by the Application:
            </p>
            <ul>
              <li>Google Play Services</li>
            </ul>
            
            <p>The Service Provider may disclose User Provided and Automatically Collected Information:</p>
            <ul>
              <li>as required by law, such as to comply with a subpoena, or similar legal process;</li>
              <li>when they believe in good faith that disclosure is necessary to protect their rights, protect your safety or the safety of others, investigate fraud, or respond to a government request;</li>
              <li>with their trusted services providers who work on their behalf, do not have an independent use of the information we disclose to them, and have agreed to adhere to the rules set forth in this privacy statement.</li>
            </ul>
          </section>

          <section>
            <h2>Opt-Out Rights</h2>
            <p>
              You can stop all collection of information by the Application easily by uninstalling it. You may use the standard uninstall processes as may be available as part of your mobile device or via the mobile application marketplace or network.
            </p>
          </section>

          <section id="data-deletion">
            <h2>Data Retention & User Data Deletion Policy</h2>
            <p>
              The Service Provider will retain User Provided data for as long as you use the Application and for a reasonable time thereafter. JustConverter operates with on-device image processing; your images and converted files are processed directly on your Android phone and are not uploaded or saved on external servers.
            </p>
            <p>
              If you wish to request the deletion of any user records, feedback notes, or support messages provided via JustConverter, you have full freedom to request deletion at any time.
            </p>

            <div className="data-deletion-card">
              <div className="data-deletion-header">
                <Trash2 size={24} color="#dc2626" />
                <h3>Request JustConverter Data Deletion</h3>
              </div>
              <p>
                To request permanent deletion of your data, please send an email to our support team at:
              </p>
              <p style={{ fontSize: '1.05rem', fontWeight: 600 }}>
                <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a>
              </p>
              <p>
                Please mention "JustConverter Data Deletion Request" in the subject line and include your registered email address or device details. We will process and permanently remove your data within <strong>48 to 72 business hours</strong>.
              </p>
              <a 
                href="mailto:pankajacharyaorg@gmail.com?subject=JustConverter%20Data%20Deletion%20Request&body=Hello%20JustLabs%20Support%2C%0A%0AI%20would%20like%20to%20request%20the%20permanent%20deletion%20of%20all%20my%20data%20associated%20with%20JustConverter.%0A%0AMy%20details%3A%0A-%20Full%20Name%3A%20%0A-%20Registered%20Email%20Address%3A%20%0A-%20Device%20Identifier%20%2F%20Details%3A%20%0A-%20Additional%20Notes%3A%20%0A%0AThank%20you."
                className="data-deletion-btn"
              >
                <Send size={16} />
                Send Data Deletion Email
              </a>
              <div className="data-deletion-email-direct">
                Direct Contact: <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a>
              </div>
            </div>
          </section>

          <section>
            <h2>Children</h2>
            <p>
              The Service Provider does not use the Application to knowingly solicit data from or market to children under the age of 13.
            </p>
            
            <p>
              The Application does not address anyone under the age of 13. The Service Provider does not knowingly collect personally identifiable information from children under 13 years of age. In the case the Service Provider discover that a child under 13 has provided personal information, the Service Provider will immediately delete this from their servers. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact the Service Provider (<a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a>) so that they will be able to take the necessary actions.
            </p>
          </section>

          <section>
            <h2>Security</h2>
            <p>
              The Service Provider is concerned about safeguarding the confidentiality of your information. The Service Provider provides physical, electronic, and procedural safeguards to protect information the Service Provider processes and maintains.
            </p>
          </section>

          <section>
            <h2>Changes</h2>
            <p>
              This Privacy Policy may be updated from time to time for any reason. The Service Provider will notify you of any changes to the Privacy Policy by updating this page with the new Privacy Policy. You are advised to consult this Privacy Policy regularly for any changes, as continued use is deemed approval of all changes.
            </p>
            <p>This privacy policy is effective as of 2025-03-15</p>
          </section>

          <section>
            <h2>Your Consent</h2>
            <p>
              By using the Application, you are consenting to the processing of your information as set forth in this Privacy Policy now and as amended by us.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              If you have any questions regarding privacy while using the Application, or have questions about the practices, please contact the Service Provider via email at <a href="mailto:pankajacharyaorg@gmail.com">pankajacharyaorg@gmail.com</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default JustConverterPrivacyPolicy;
